import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { LocaleProvider } from '@xdocs/ui';
import { isLocale, negotiateLocale } from '@xdocs/i18n';
import type { Locale } from '@xdocs/i18n';

const LOCALE_STORAGE_KEY = 'xdocs.locale';

function readStoredLocale(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode / disabled) — fall through to negotiation.
    return null;
  }
}

function detectInitialLocale(): Locale {
  const stored = readStoredLocale();
  const acceptLanguage =
    typeof navigator !== 'undefined' ? (navigator.languages?.join(',') ?? navigator.language) : null;
  return negotiateLocale({ persisted: stored, acceptLanguage });
}

interface LocaleControllerContextValue {
  setLocale: (locale: Locale) => void;
}

const LocaleControllerContext = createContext<LocaleControllerContextValue | null>(null);

export interface LocaleControllerProps {
  children: ReactNode;
}

/**
 * Owns `users.locale` persistence for `apps/web` (F0j/E18 §20.5-6 detection
 * order: explicit switcher choice → persisted → `/ro` route prefix (landing
 * only) → `Accept-Language` → `en`). `@xdocs/i18n`/`@xdocs/ui` are leaf
 * packages with no storage of their own (§3 dependency direction) — this is
 * the host-app binding `CODE_GUIDELINES.md` assigns to `apps/web`.
 *
 * Once authenticated, `useSyncLocaleToServer` (see `../ws/useSyncSettingsToServer.ts`)
 * best-effort persists the choice server-side via `settings.setLocale` so it
 * survives a different device — this controller's localStorage copy is the
 * cross-reload source of truth for THIS browser regardless of auth state.
 */
export function LocaleController({ children }: LocaleControllerProps): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() => detectInitialLocale());

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    if (!isLocale(next)) return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // best-effort persistence only — the in-memory state still updates.
    }
  }, []);

  const value = useMemo<LocaleControllerContextValue>(() => ({ setLocale }), [setLocale]);

  return (
    <LocaleControllerContext.Provider value={value}>
      <LocaleProvider locale={locale}>{children}</LocaleProvider>
    </LocaleControllerContext.Provider>
  );
}

/** `const { setLocale } = useLocaleController();` — pair with `@xdocs/ui`'s
 * `useI18n()` for the current `{ t, locale }`. */
export function useLocaleController(): LocaleControllerContextValue {
  const ctx = useContext(LocaleControllerContext);
  if (!ctx) throw new Error('useLocaleController must be used within a <LocaleController>');
  return ctx;
}
